import { useMemberById, usePrivileges } from '@/shared/hooks/useMembers'
import type { MemberPrivileges } from '@/shared/models/memberModel'
import { isEmpty } from 'lodash'
import { useTranslations } from 'next-intl'
import type { DropdownMenuProps } from './DropDownMenu'
import DropdownMenu from './DropDownMenu'

type DropdownItem = Omit<DropdownMenuProps['items'][number], 'key'>
export type DropdownItemWithKey = { key: string } & DropdownItem

type ItemKeys<T extends Array<{ key: string }>> = T[number]['key']

export type PrivilegesDropdownProps<T extends Array<DropdownItemWithKey> = Array<DropdownItemWithKey>> = Omit<
    DropdownMenuProps,
    'items' | 'isLoading'
> & {
    isLoading?: boolean
    requiredPrivilege?: MemberPrivileges
    unauthorizedMessage?: string
    items: T
    authorizedActions?: Array<ItemKeys<T>>
}

const NO_AUTHORIZED_ACTIONS: never[] = []

function PrivilegesDropdown<T extends Array<DropdownItemWithKey>>({
    requiredPrivilege = 'readwrite',
    authorizedActions = NO_AUTHORIZED_ACTIONS,
    items,
    ...props
}: PrivilegesDropdownProps<T>) {
    const t = useTranslations('root')

    const unauthorizedMessage = props?.unauthorizedMessage ?? t('privileges.button')
    const member = useMemberById()
    const memberData = member.data

    const { isAuthorized } = usePrivileges({
        member: memberData,
        requiredPrivilege
    })

    const mappedItems: DropdownMenuProps['items'] = items.map(({ key, ...item }) => {
        const requiresAuthorization = isEmpty(authorizedActions) ? true : authorizedActions.includes(key)
        const hasPermission = requiresAuthorization ? isAuthorized : true

        return {
            ...item,
            key,
            isDisabled: item.isDisabled || !hasPermission,
            tooltip: item.tooltip || (hasPermission ? undefined : unauthorizedMessage)
        }
    })

    return <DropdownMenu {...props} items={mappedItems} />
}

export default PrivilegesDropdown
